import { flashFeed } from "@/mocks/homeData";

const toneStyle: Record<string, { dot: string; label: string }> = {
  hot: { dot: "bg-red-600", label: "text-red-600 bg-red-50 border-red-200" },
  rise: { dot: "bg-orange-500", label: "text-orange-600 bg-orange-50 border-orange-200" },
  normal: { dot: "bg-stone-400", label: "text-stone-600 bg-stone-50 border-stone-200" },
  down: { dot: "bg-stone-300", label: "text-stone-400 bg-stone-50 border-stone-200" },
};

export default function FlashFeed() {
  return (
    <section className="bg-stone-950 text-stone-100">
      <div className="mx-auto max-w-[1440px] px-4 md:px-6 py-12 md:py-16">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs font-bold tracking-[0.2em] text-red-400">
              <i className="ri-radio-2-fill"></i> 扩散快讯流
            </div>
            <h2 className="mt-3 font-serif text-2xl font-black tracking-tight text-white md:text-4xl">
              正在发生 · 实时扩散动态
            </h2>
            <p className="mt-2 text-sm leading-6 text-stone-400">
              追踪热搜登顶前的每一个关键节点，滚动更新，信号级还原一场转发刷屏的现场节奏。
            </p>
          </div>
          <div className="inline-flex items-center gap-2 self-start rounded-md border border-red-500/40 bg-red-600/10 px-3 py-1.5 text-xs font-semibold text-red-400 md:self-auto">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500"></span>
            </span>
            监测信号更新中
          </div>
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-2">
          {/* 快讯列表 */}
          <div className="rounded-lg border border-stone-800 bg-stone-900/50 p-1.5">
            {flashFeed.map((item, i) => {
              const tone = toneStyle[item.tone];
              return (
                <div
                  key={i}
                  className={`flex items-start gap-3 px-3 py-3 transition-colors hover:bg-stone-800/60 ${
                    i !== flashFeed.length - 1 ? "border-b border-stone-800/70" : ""
                  }`}
                >
                  <span className={`mt-2 h-2 w-2 shrink-0 rounded-full ${tone.dot}`}></span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-xs font-semibold text-stone-400">{item.time}</span>
                      <span className={`whitespace-nowrap rounded border px-1.5 py-0.5 text-[11px] font-bold ${tone.label}`}>
                        {item.tag}
                      </span>
                    </div>
                    <p className="mt-1.5 text-sm leading-6 text-stone-200">{item.text}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* 数据看板 */}
          <div className="flex flex-col">
            <div className="rounded-t-lg border border-stone-800 bg-stone-900/50 p-5">
              <div className="flex items-center justify-between">
                <div className="text-sm font-bold text-white">近 7 日扩散总览</div>
                <a href="#" className="text-xs font-semibold text-red-400 hover:text-red-300">
                  完整报告 <i className="ri-arrow-right-line"></i>
                </a>
              </div>
              <p className="mt-1 text-xs text-stone-500">全网多平台监测口径汇总 · 仅作热度观察</p>
            </div>
            <div className="grid flex-1 grid-cols-2 rounded-b-lg border border-t-0 border-stone-800 bg-stone-900/50">
              {[
                { num: "4.2", unit: "亿", label: "全网阅读增量", icon: "ri-eye-2-line" },
                { num: "873", unit: "万", label: "讨论与转评峰值", icon: "ri-chat-forward-line" },
                { num: "31", unit: "条", label: "登顶引爆路径", icon: "ri-fire-line" },
                { num: "12.6", unit: "小时", label: "平均引爆时长", icon: "ri-time-line" },
              ].map((s) => (
                <div
                  key={s.label}
                  className="flex flex-col justify-center p-5 odd:border-r even:border-l border-stone-800"
                >
                  <div className="flex items-center gap-2 text-stone-400">
                    <i className={`${s.icon} text-red-500`}></i>
                    <span className="text-xs">{s.label}</span>
                  </div>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="font-serif text-3xl font-black text-white md:text-4xl">{s.num}</span>
                    <span className="text-sm font-bold text-red-400">{s.unit}</span>
                  </div>
                </div>
              ))}
            </div>
            {/* 登顶走势 */}
            <div className="mt-4 rounded-lg border border-stone-800 bg-stone-900/50 p-5">
              <div className="flex items-center justify-between text-xs text-stone-400">
                <span className="font-semibold text-white">主榜首位时长曲线</span>
                <span>单话题最长 6h</span>
              </div>
              <div className="mt-4 flex h-20 items-end gap-1.5">
                {[30, 55, 42, 70, 48, 85, 60, 96, 74, 52, 66, 40, 58, 78, 90].map((h, i) => (
                  <div
                    key={i}
                    style={{ height: `${h}%` }}
                    className={`flex-1 rounded-t-sm ${
                      h >= 90 ? "bg-red-600" : h >= 70 ? "bg-orange-500/80" : "bg-stone-700"
                    }`}
                  ></div>
                ))}
              </div>
              <div className="mt-2 flex items-center justify-between text-[10px] text-stone-500">
                <span>00:00</span>
                <span className="text-red-400">当前登顶峰值 96%</span>
                <span>24:00</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}